import { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";
import { trackEvent } from "@/utils/tracking";

const DISMISS_KEY = "pwaInstallDismissedAt";
const DISMISS_DAYS = 14;
const MIN_ENGAGEMENT_MS = 20_000; // show after 20s on-site

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // Chrome / Android
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) return true;
  // iOS Safari
  if (window.navigator?.standalone) return true;
  return false;
}

function isDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Sitewide install prompt. Non-intrusive:
 *   - Hidden if already installed, if dismissed in the last 14 days,
 *     or if the user has been on the site <20s.
 *   - Captures Chrome/Android `beforeinstallprompt` for one-tap install.
 *   - iOS shows a friendly "Add to Home Screen" instructions card.
 */
export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    let engagementTimer = null;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      // Only reveal AFTER engagement timer fires, so the prompt doesn't nag
      // first-time visitors in the first 5 seconds.
      engagementTimer = setTimeout(() => setVisible(true), MIN_ENGAGEMENT_MS);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS doesn't fire beforeinstallprompt — show the instructions card
    // after 20s on-site if we detect iOS Safari.
    if (isIos()) {
      engagementTimer = setTimeout(() => {
        setShowIosHelp(true);
        setVisible(true);
      }, MIN_ENGAGEMENT_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (engagementTimer) clearTimeout(engagementTimer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* storage disabled — session-only dismiss */
    }
    setVisible(false);
    trackEvent("pwa_install_dismiss", { ios: showIosHelp });
  };

  const install = async () => {
    if (!deferredEvt) return;
    trackEvent("pwa_install_prompt_shown");
    deferredEvt.prompt();
    const choice = await deferredEvt.userChoice;
    trackEvent("pwa_install_choice", { outcome: choice?.outcome });
    setDeferredEvt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Install I.T. Help app"
      className="fixed bottom-24 right-4 left-4 sm:left-auto sm:w-96 z-[55] animate-fade-in-up"
      style={{ animationDuration: "0.4s" }}
    >
      <div
        className="glass-card border border-blue-500/30 rounded-2xl p-4 shadow-2xl relative overflow-hidden"
        style={{ boxShadow: "0 25px 60px -15px rgba(59, 130, 246, 0.4)" }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          data-testid="pwa-install-dismiss"
          className="absolute top-2 right-2 inline-flex items-center justify-center min-w-[36px] min-h-[36px] rounded-full text-slate-500 hover:text-white hover:bg-white/5 transition"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-400/30 flex items-center justify-center">
            <Download size={22} className="text-blue-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white leading-tight">
              Install the I.T. Help app
            </p>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">
              {showIosHelp
                ? "One-tap self-help, booking, and emergency help from your home screen."
                : "Add to your home screen for one-tap self-help, booking, and emergency support."}
            </p>
          </div>
        </div>

        {showIosHelp ? (
          <div className="mt-4 pt-4 border-t border-white/5">
            <p className="text-[11px] font-bold tracking-[0.15em] uppercase text-blue-400 mb-2.5">
              Add to Home Screen on iPhone
            </p>
            <ol className="space-y-2 text-xs text-slate-300 leading-relaxed">
              <li className="flex items-center gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white/5 border border-white/10 text-[10px] inline-flex items-center justify-center font-semibold">1</span>
                <span className="flex items-center gap-1.5 flex-wrap">
                  Tap the <Share size={14} className="inline text-blue-400" /> Share button
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white/5 border border-white/10 text-[10px] inline-flex items-center justify-center font-semibold">2</span>
                <span className="flex items-center gap-1.5 flex-wrap">
                  Scroll down, tap <strong className="text-white">Add to Home Screen</strong> <Plus size={14} className="inline text-blue-400" />
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white/5 border border-white/10 text-[10px] inline-flex items-center justify-center font-semibold">3</span>
                <span>Tap <strong className="text-white">Add</strong> — done.</span>
              </li>
            </ol>
          </div>
        ) : (
          <button
            type="button"
            onClick={install}
            data-testid="pwa-install-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors btn-glow"
          >
            <Download size={16} /> Install Now
          </button>
        )}
      </div>
    </div>
  );
}
